import type { CSSProperties } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * Shared geometry for standard pill CTAs (auth, workforce hub, modals).
 * Size tokens live in `src/styles/globals.css` (`--control-button-*`) so one edit propagates.
 */
export const APP_ACTION_BUTTON_SHELL_CLASS = cn(
  'inline-flex min-h-[var(--control-button-height-md)] items-center justify-center gap-2.5',
  'px-[var(--control-button-padding-x-md)] py-1 text-sm font-medium',
  'rounded-[var(--control-button-radius)]',
  'active:scale-[0.99]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

/**
 * White label + interaction shell (no fill). Pair with gradient `style` or custom fills
 * (e.g. {@link GuardianDashLinkButton} teal/coral).
 */
export const PRIMARY_ACTION_BUTTON_LAYOUT_CLASS = cn(APP_ACTION_BUTTON_SHELL_CLASS, 'text-white');

/**
 * Full primary (ink) pill — {@link AuthPrimaryButton}, modal confirm, hub solid CTAs without gradient.
 */
export const PRIMARY_ACTION_BUTTON_BASE_CLASS = cn(
  APP_ACTION_BUTTON_SHELL_CLASS,
  'bg-action text-white transition-[background-color,transform] hover:bg-action-hover',
);

/**
 * Workforce hub navy pill: empty states, toolbar CTAs (Create Care Team, Onboard New Staff).
 * Use {@link WORKFORCE_FORM_FOOTER_SUBMIT_BUTTON_CLASS} for New Staff / New Care Team submit rows (adds min width).
 */
export const WORKFORCE_HUB_PRIMARY_PILL_CLASS = cn(
  PRIMARY_ACTION_BUTTON_BASE_CLASS,
  'shadow-[0px_2px_4px_0px_rgba(39,39,39,0.1)]',
);

/** New Staff / New Care Team form footer primary submit — same pill as hub CTAs with a stable minimum width. */
export const WORKFORCE_FORM_FOOTER_SUBMIT_BUTTON_CLASS = cn(WORKFORCE_HUB_PRIMARY_PILL_CLASS, 'min-w-[10.5rem]');

/**
 * Teal gradient pill (brand → brand-deep). Same stops as {@link OnboardingBrandGradientButton};
 * apply {@link BRAND_GRADIENT_PRIMARY_PILL_STYLE} via `style` for the gradient fill.
 */
export const BRAND_GRADIENT_PRIMARY_PILL_CLASS = cn(
  PRIMARY_ACTION_BUTTON_LAYOUT_CLASS,
  'transition-[opacity,transform] hover:opacity-95',
);

export const BRAND_GRADIENT_PRIMARY_PILL_STYLE = {
  backgroundImage: 'linear-gradient(114.08deg, var(--color-brand) 4.5%, var(--color-brand-deep) 96.85%)',
} as const satisfies Pick<CSSProperties, 'backgroundImage'>;

/**
 * White pill with hairline border — onboarding skip, modal cancel, outline actions.
 */
export const OUTLINE_PILL_BUTTON_CLASS = cn(
  APP_ACTION_BUTTON_SHELL_CLASS,
  'border-[0.5px] border-[rgba(37,49,72,0.2)] bg-white text-ink',
  'transition-[background-color,transform] hover:bg-[rgba(15,27,49,0.04)]',
);
